'use client';

import React, { Suspense } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';

interface BreadcrumbItem {
  title: string;
  href: string;
  icon?: boolean;
  current?: boolean;
}

// Separate component that uses useSearchParams
function BreadcrumbsContent() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Clean up the pathname to ensure no double slashes or trailing slashes
  const cleanPathname = (pathname || '/').replace(/\/+/g, '/').replace(/\/$/, '');
  const pathSegments = cleanPathname.split('/').filter(segment => segment && segment !== 'apps');
  
  // Check if we're on a campsite detail page AND have search params
  const isOnCampsiteDetail = pathSegments.includes('camp-sites');
  const hasSearchParams = (searchParams?.toString() || '').length > 0;
  const showBackToSearch = isOnCampsiteDetail && hasSearchParams;

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [
      { title: 'Home', href: '/', icon: true }
    ];

    let currentPath = '';

    // Process path segments
    pathSegments.forEach((segment, index) => {
      // Clean the segment to ensure no slashes
      segment = segment.trim();
      
      // Build the path properly without showing it in the title
      if (index === 0) {
        currentPath = `/${segment}`;
      } else {
        currentPath = `${currentPath}/${segment}`;
      }

      if (segment === 'campsite-explorer') {
        // Only show as breadcrumb if we're deeper than the main page
        if (pathSegments.length > 1) {
          items.push({ 
            title: 'Campsite Explorer', 
            href: '/apps/campsite-explorer'
          });
        }
      } else if (segment === 'regions') {
        if (index < pathSegments.length - 1 || pathSegments.length > 2) {
          items.push({ 
            title: 'Regions', 
            href: '/apps/campsite-explorer/regions'
          });
        } else {
          items.push({ 
            title: 'Regions', 
            href: '/apps/campsite-explorer/regions',
            current: true
          });
        }
      } else if (segment === 'camp-sites') {
        // Skip camp-sites segment - handled by "Back to Search" when applicable
      } else if (index === pathSegments.length - 1 && pathSegments[index - 1] === 'regions') {
        // This is a region detail page
        const title = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        items.push({ 
          title, 
          href: cleanPathname,
          current: true 
        });
      } else if (index === pathSegments.length - 1 && pathSegments[index - 1] === 'camp-sites') {
        // This is the campsite name - add it to breadcrumbs
        const title = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        items.push({ 
          title, 
          href: cleanPathname,
          current: true 
        });
      }
    });

    return items;
  };

  const renderHomeLink = () => {
    const handleHomeClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (pathname === '/apps/campsite-explorer') {
        window.location.reload();
      } else {
        router.push('/apps/campsite-explorer');
      }
    };

    return (
      <a 
        href="/apps/campsite-explorer"
        onClick={handleHomeClick}
        className="breadcrumb-link"
        aria-label="Campsite Explorer Home"
      >
        <svg className="home-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
        </svg>
      </a>
    );
  };

  // Generate the back to search URL with preserved params
  const backToSearchUrl = `/apps/campsite-explorer${hasSearchParams ? `?${searchParams?.toString() || ''}` : ''}`;

  const handleBackToSearch = (e: React.MouseEvent) => {
    e.preventDefault();
    router.back(); // Use browser back to maintain history
  };

  return (
    <div className="breadcrumbs-wrapper">
      <nav className="modern-breadcrumbs" role="navigation" aria-label="breadcrumbs">
        <div className="breadcrumbs-container">
          <div className="breadcrumbs-content">
            {/* Back to Search - Left Side */}
            {showBackToSearch && (
              <>
                <a 
                  href={backToSearchUrl}
                  onClick={handleBackToSearch}
                  className="back-to-search-link"
                >
                  <svg className="back-arrow-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                  Back to Search Results
                </a>
                <div className="vertical-separator"></div>
              </>
            )}
            
            {/* Regular Breadcrumbs - Also Left Side */}
            <ol className="breadcrumb-list">
              {getBreadcrumbItems().map((item, index, array) => (
                <li key={item.href || index} className="breadcrumb-item">
                  {item.icon ? (
                    renderHomeLink()
                  ) : (
                    <Link 
                      href={item.href}
                      className={`breadcrumb-link ${item.current ? 'current' : ''}`}
                      aria-current={item.current ? 'page' : undefined}
                    >
                      {item.title}
                    </Link>
                  )}
                  {index < array.length - 1 && !item.current && (
                    <svg className="separator-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </nav>

      <style jsx>{`
        .breadcrumbs-wrapper {
          background: #f8f9fa;
          border-bottom: 1px solid #eee;
        }

        .modern-breadcrumbs {
          padding: 12px 0;
          font-size: 14px;
          color: #666;
        }

        .breadcrumbs-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .breadcrumbs-content {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0;
        }

        :global(.back-to-search-link) {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #1e40af;
          text-decoration: none;
          transition: color 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
          margin-right: 16px;
        }

        :global(.back-to-search-link:hover) {
          color: #1e3a8a;
        }

        .vertical-separator {
          width: 1px;
          height: 20px;
          background-color: #ddd;
          margin-right: 16px;
          flex-shrink: 0;
        }

        .breadcrumb-list {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          margin: 0;
          padding: 0;
          list-style: none;
          gap: 4px;
        }

        .breadcrumb-list a::before {
          content: none !important;
        }

        .breadcrumb-item {
          display: flex;
          align-items: center;
        }

        :global(.breadcrumb-link) {
          display: flex;
          align-items: center;
          color: #666;
          text-decoration: none;
          transition: color 0.2s ease;
          white-space: nowrap;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        :global(.breadcrumb-link:hover) {
          color: #DC2626;
        }

        :global(.breadcrumb-link.current) {
          color: #111;
          font-weight: 500;
          max-width: none;
        }

        :global(.home-icon) {
          stroke: currentColor;
          width: 20px;
          height: 20px;
        }

        :global(.back-arrow-icon) {
          stroke: currentColor;
          width: 16px;
          height: 16px;
        }

        :global(.separator-icon) {
          margin: 0 6px;
          stroke: #999;
          flex-shrink: 0;
        }

        .visually-hidden {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          border: 0;
        }

        @media (max-width: 768px) {
          .modern-breadcrumbs {
            padding: 8px 0;
            font-size: 12px;
          }

          .breadcrumbs-container {
            padding: 0 16px;
          }

          .breadcrumbs-content {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          :global(.back-to-search-link) {
            font-size: 14px;
            margin-right: 0;
            margin-bottom: 8px;
          }

          .vertical-separator {
            display: none;
          }

          .breadcrumb-list {
            margin-left: 0;
          }

          .breadcrumb-link {
            max-width: 150px;
          }
        }

        @media (prefers-color-scheme: dark) {
          .breadcrumbs-wrapper {
            background: #1a1a1a;
            border-color: #333;
          }

          .vertical-separator {
            background-color: #444;
          }

          :global(.breadcrumb-link) {
            color: #999;
          }

          :global(.breadcrumb-link.current) {
            color: #fff;
          }

          :global(.back-to-search-link) {
            color: #60a5fa;
          }

          :global(.back-to-search-link:hover) {
            color: #93c5fd;
          }

          :global(.separator-icon) {
            stroke: #666;
          }
        }
      `}</style>
    </div>
  );
}

// Main component with Suspense boundary
const Breadcrumbs = () => {
  return (
    <Suspense fallback={
      <div className="breadcrumbs-wrapper" style={{ background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
        <nav className="modern-breadcrumbs" style={{ padding: '12px 0' }}>
          <div className="breadcrumbs-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
            <div style={{ height: '20px', background: '#e5e7eb', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
          </div>
        </nav>
      </div>
    }>
      <BreadcrumbsContent />
    </Suspense>
  );
};

export default Breadcrumbs;